import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import Button from "@mui/material/Button";
import { SpinnerCircular } from "spinners-react";

import TextField from "@mui/material/TextField";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import useHttp from "../../../hooks/useHttp";
import { fetchBasicInfo } from "../../../store/slices/basicInfo";
import "../../../assets/css/utility/input-field.css";
import "../../../assets/css/user_auth_pages.css";
import Styles from "./Login.module.css";
import SuccessMessage from "../../../components/SuccessMessage";
import { LOGIN_URL } from "../../../utils/api-ends";
import { authAnimationPoint } from "../../../store/slices/authAnimation";
import { gsap } from "gsap";

const Login = ({ authContainer }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [emailError, setEmailError] = useState([]);
  const [passwordError, setPasswordError] = useState([]);
  const [errorMessages, setErrorMessages] = useState([]);
  const [successMessage, setSuccessMessage] = useState(null);

  const [showPassword, setShowPassword] = useState(false);
  const [nextPage, setNextPage] = useState(false);

  const { error, isLoading, sendRequests } = useHttp();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const onEmailChange = (e) => setEmail(e.target.value);
  const onPasswordChange = (e) => setPassword(e.target.value);

  const viewPassword = () => setShowPassword(!showPassword);

  let auth_tl = gsap.timeline();

  const handleNextPage = () => {
    setEmailError([]);
    setPasswordError([]);

    if (email?.trim()?.length === 0) {
      setEmailError(["This field is required"]);
      return;
    }

    setNextPage(true);
    dispatch(authAnimationPoint(true));

    // for animation
    auth_tl
      .to(authContainer.current, { x: -1000, opacity: 0, duration: 0.1 })
      .to(authContainer.current, { x: 1000, duration: 0.1 })
      .to(authContainer.current, { x: 0, opacity: 1, duration: 0.1 });
  };

  const handleBack = () => {
    dispatch(authAnimationPoint(false));
    setNextPage(false);

    // for animation
    auth_tl
      .to(authContainer.current, { x: 1000, opacity: 0, duration: 0.1 })
      .to(authContainer.current, { x: -1000, duration: 0.1 })
      .to(authContainer.current, { x: 0, opacity: 1, duration: 0.1 });
  };

  const onSubmit = (e) => {
    if (isLoading) return false;

    setEmailError([]);
    setPasswordError([]);
    setErrorMessages([]);

    if (password?.trim()?.length === 0) {
      setPasswordError(["This field is required"]);
      return;
    }

    const response = (data) => {
      setSuccessMessage("Successfully logged in");

      localStorage.setItem(
        "auth_token",
        JSON.stringify({
          access: data.access,
          refresh: data.refresh,
        })
      );

      // Assign user info to store
      dispatch(
        fetchBasicInfo({
          isAuthenticated: true,
          userInfo: data?.user_info,
          latestChatRooms: data?.chat_rooms_info,
        })
      );

      // Redirect to profile page
      setTimeout(() => {
        if(data.user_info?.is_account_verified === false){
          if (data.user_info.have_to_verify === "both") {
            navigate("/auth/account-verification?to_verify=both");
          } else if (data.user_info.have_to_verify === "email") {
            navigate("/auth/account-verification?to_verify=email");
          } else if (data.user_info.have_to_verify === "phone") {
            navigate("/auth/account-verification?to_verify=phone");
          }
        }else{
          navigate("/profile-information/")
        }
      }, 800); 
    };

    sendRequests(
      {
        method: "post",
        url: LOGIN_URL,
        data: {
          email,
          password,
        },
      },
      response
    );
  };

  useEffect(() => {
    if (error) {
      if (error.password) {
        setPasswordError(error.password);
      }
      if (error.email) {
        setEmailError(error.email);
      }
      if (error.detail) {
        setErrorMessages([error.detail]);
      }
      if (error.general) {
        setErrorMessages([error.general]);
      }
    }
  }, [error]);

  return (
    <>
      <p className="auth-instruction"></p>

      <div className="user-input-field">
        {/* Error Messages  */}
        {nextPage === true &&
          errorMessages?.map((error, index) => (
            <p className="auth-error" key={index}>
              {error}
            </p>
          ))}

        {/* Success Message */}
        {nextPage === true && successMessage && (
          <SuccessMessage message={successMessage} />
        )}

        {!nextPage && (
          <div className="input-box-area">
            {emailError?.map((error, index) => (
              <p className="auth-error" key={index}>
                {error}
              </p>
            ))}

            <TextField
              fullWidth
              autoFocus
              className="the-mui-input-field"
              onChange={onEmailChange}
              label="Email Address or Phone Number"
              variant="outlined"
              value={email}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleNextPage();
              }}
            />
          </div>
        )}

        {nextPage && (
          <div className="input-box-area">
            {passwordError?.map((error, index) => (
              <p className="auth-error" key={index}>
                {error}
              </p>
            ))}

            <TextField
              fullWidth
              autoFocus
              className="the-mui-input-field"
              onChange={onPasswordChange}
              label="Password"
              variant="outlined"
              type={showPassword ? "text" : "password"}
              value={password}
              onKeyDown={(e) => {
                if (e.key === "Enter") onSubmit();
              }}
            />
            <span onClick={viewPassword}>
              <FontAwesomeIcon
                icon={`fa-solid ${showPassword ? "fa-eye" : "fa-eye-slash"}`}
              />
            </span>
          </div>
        )}

        {!nextPage ? (
          <Button className="theme-btn submit-btn" onClick={handleNextPage}>
            Next
          </Button>
        ) : (
          <div className={Styles.ButtonContainer}>
            <Button
              className={`theme-btn submit-btn ${Styles.BackButton}`}
              onClick={handleBack}
            >
              <FontAwesomeIcon
                icon="fa-solid fa-angles-left"
                className={Styles.BackIcon}
              />
              Back
            </Button>

            <Button className="theme-btn submit-btn" onClick={onSubmit}>
              Submit
              {isLoading && (
                <SpinnerCircular
                  size={22}
                  thickness={180}
                  speed={130}
                  color="#fff"
                  className="btn-loader"
                  secondaryColor="rgba(57, 172, 127, 0.44)"
                />
              )}
            </Button>
          </div>
        )}
      </div>

      <Link to="/auth/forget-password" className="auth-usefull-links">
        Forget Password ?
      </Link>

      <Link to="/auth/sign-up" className="auth-usefull-links">
        Create an Account
      </Link>
    </>
  );
};

export default Login;
